import { toast } from 'react-toastify';
import React, { useState, useEffect, useCallback } from 'react';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { lockerFloorService, lockerTypeService, lockerAddonService, lockerSpaceService } from '../services/lockerService';
import { LockerSpace, LockerType, LockerAddon } from '../types/locker';
import FloorLayoutViewer from './FloorLayoutViewer';
import FabricFloorLayoutEditor from './FabricFloorLayoutEditor';
import LockerSpaceEditModal from './LockerSpaceEditModal';
import BulkSpaceModal from './BulkSpaceModal';
import TypeLegend from './TypeLegend';
import { FloorSpace, SpaceUpdateData } from '../types/floorLayout';
import '../styles/ModelDetailsSlider.css';
import '../styles/DataTable.css';

interface LockerFloor {
  _id: string;
  name: string;
  projectId: string;
  projectName?: string;
  layout?: {
    url: string;
    key?: string;
    width: number;
    height: number;
    size?: number;
    mimeType?: string;
  };
  spaceDefaults?: {
    width: number;
    height: number;
  };
  spaces?: {
    total?: number;
    available?: number;
    marketingHold?: number;
    salesHold?: number;
    sold?: number;
    customerSelection?: number;
    customerSubmitted?: number;
  };
  // Fallback for flat structure (if API returns it differently)
  total?: number;
  available?: number;
  marketingHold?: number;
  salesHold?: number;
  sold?: number;
  customerSelection?: number;
  customerSubmitted?: number;
}

interface LockerFloorDetailsSliderProps {
  workspaceId: string;
  lockerFloorId: string;
  onClose: () => void;
}

type TabType = 'floor-layout' | 'locker-spaces';

const LockerFloorDetailsSlider: React.FC<LockerFloorDetailsSliderProps> = ({
  workspaceId,
  lockerFloorId,
  onClose
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('floor-layout');
  const [lockerFloor, setLockerFloor] = useState<LockerFloor | null>(null);
  const [lockerSpaces, setLockerSpaces] = useState<LockerSpace[]>([]);
  const [lockerTypes, setLockerTypes] = useState<LockerType[]>([]);
  const [lockerAddons, setLockerAddons] = useState<LockerAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const [error, setError] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<LockerSpace | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  useEffect(() => {
    fetchLockerFloor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockerFloorId]);

  useEffect(() => {
    if (activeTab === 'locker-spaces' || activeTab === 'floor-layout') {
      fetchLockerSpaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    // Disable body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      // Re-enable body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  const fetchLockerFloor = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await lockerFloorService.getById(workspaceId, lockerFloorId);
      setLockerFloor(response.lockerFloor);

      // Fetch locker types and addons for the project - await both to ensure they load before rendering
      if (response.lockerFloor.projectId) {
        await Promise.all([
          fetchLockerTypes(response.lockerFloor.projectId),
          fetchLockerAddons(response.lockerFloor.projectId)
        ]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch locker floor details');
      console.error('Error fetching locker floor:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLockerTypes = async (projectId: string) => {
    try {
      const response = await lockerTypeService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setLockerTypes(response.lockerTypes || []);
    } catch (err: any) {
      console.error('Error fetching locker types:', err);
    }
  };

  const fetchLockerAddons = async (projectId: string) => {
    try {
      const response = await lockerAddonService.getAll(workspaceId, {
        projectId,
        limit: 1000
      });
      setLockerAddons(response.lockerAddons || []);
    } catch (err: any) {
      console.error('Error fetching locker addons:', err);
    }
  };

  const fetchLockerSpaces = async () => {
    try {
      setLoadingSpaces(true);
      const { lockerSpaceService } = await import('../services/lockerService');
      const response = await lockerSpaceService.getByFloor(workspaceId, lockerFloorId);
      setLockerSpaces(response.lockerSpaces);
    } catch (err: any) {
      console.error('Error fetching locker spaces:', err);
    } finally {
      setLoadingSpaces(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'AVAILABLE':
        return 'status-badge badge-available';
      case 'MARKETING HOLD':
        return 'status-badge badge-marketing-hold';
      case 'SALES HOLD':
        return 'status-badge badge-sales-hold';
      case 'CUSTOMER SELECTION':
        return 'status-badge badge-customer-selection';
      case 'CUSTOMER SUBMITTED':
        return 'status-badge badge-customer-submitted';
      case 'SOLD':
        return 'status-badge badge-sold';
      default:
        return 'status-badge';
    }
  };

  const handleSavePositions = async (updates: SpaceUpdateData[]) => {
    try {
      const { lockerSpaceService } = await import('../services/lockerService');
      // Batch update locker space positions
      const updateData = updates.map(update => ({
        id: update.id,
        spaceObject: update.spaceObject
      }));
      await lockerSpaceService.bulkUpdate(workspaceId, updateData);
      // Refresh spaces and locker floor data
      await fetchLockerSpaces();
      await fetchLockerFloor();
      // Don't close editor automatically after save
    } catch (err: any) {
      console.error('Error saving positions:', err);
      throw err;
    }
  };

  const handleSpaceClick = (spaceId: string) => {
    const space = lockerSpaces.find(s => s._id === spaceId);
    if (space) {
      handleEditSpace(space);
    }
  };

  const handleViewSpaceClick = useCallback((spaceId: string) => {
    const space = lockerSpaces.find(s => s._id === spaceId);
    if (space) {
      setSelectedSpace(space);
      setShowViewModal(true);
    }
  }, [lockerSpaces]);

  const handleEditSpace = (space: LockerSpace) => {
    setSelectedSpace(space);
    setShowEditModal(true);
  };

  const handleDeleteSpace = async (spaceId: string) => {
    if (!window.confirm('Are you sure you want to delete this locker space?')) {
      return;
    }

    try {
      const { lockerSpaceService } = await import('../services/lockerService');
      await lockerSpaceService.delete(workspaceId, spaceId);
      await refreshAllData();
    } catch (err: any) {
      console.error('Error deleting space:', err);
      toast.error(err.response?.data?.message || 'Failed to delete locker space');
    }
  };

  const handleSpaceSaved = async () => {
    await refreshAllData();
    setShowEditModal(false);
  };

  const refreshAllData = async () => {
    await Promise.all([fetchLockerFloor(), fetchLockerSpaces()]);
  };

  const handleBulkSpaceSave = async (spaces: any[]) => {
    // Create all spaces using the locker space service
    for (const spaceData of spaces) {
      await lockerSpaceService.create(workspaceId, spaceData);
    }
    await refreshAllData();
  };

  if (loading) {
    return (
      <div className="model-details-slider">
        <div className="model-details-loading">
          <div className="builders-loading-spinner" />
        </div>
      </div>
    );
  }

  if (error || !lockerFloor) {
    return (
      <div className="model-details-slider">
        <div className="model-details-error">
          <p>{error || 'Locker floor not found'}</p>
          <button className="btn btn-secondary btn-md" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="model-details-slider">
      {/* Header */}
      <div className="model-details-header">
        <div className="model-details-header-left">
          <button className="model-details-back-btn" onClick={onClose}>
            ← Back
          </button>
          <div className="model-details-title">
            <h2>Locker Floor: {lockerFloor.name}</h2>
            <p>Project: {lockerFloor.projectName || 'N/A'}</p>
          </div>
        </div>
        <button className="model-details-close-btn" onClick={onClose}>
          ✕
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        background: '#D4CBC4',
        padding: '20px',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', gap: '8px', padding: 0 }}>
          <button
            onClick={() => setActiveTab('floor-layout')}
            style={{
              background: activeTab === 'floor-layout' ? '#48182F' : 'transparent',
              border: 'none',
              outline: 'none',
              color: activeTab === 'floor-layout' ? '#FFFFFF' : '#6B7280',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: activeTab === 'floor-layout' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'floor-layout') {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                e.currentTarget.style.color = '#48182F';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'floor-layout') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            Floor Layout
          </button>
          <button
            onClick={() => setActiveTab('locker-spaces')}
            style={{
              background: activeTab === 'locker-spaces' ? '#48182F' : 'transparent',
              border: 'none',
              outline: 'none',
              color: activeTab === 'locker-spaces' ? '#FFFFFF' : '#6B7280',
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: activeTab === 'locker-spaces' ? '600' : '500',
              cursor: 'pointer',
              borderRadius: '20px',
              transition: 'all 0.2s',
              boxShadow: 'none'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== 'locker-spaces') {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                e.currentTarget.style.color = '#48182F';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== 'locker-spaces') {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            Locker Spaces
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="model-details-content" style={{
        gridTemplateColumns: '1fr',
        background: '#D4CBC4',
        padding: '24px',
        overflow: 'auto'
      }}>
        {activeTab === 'floor-layout' && (
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <h3>Floor Layout</h3>
              <button
                onClick={() => setShowEditor(true)}
                className="btn btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Pencil size={16} /> Edit Floor Layout
              </button>
            </div>
            <div className="model-details-tile-content">
              {loadingSpaces ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '400px'
                }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '20px' }}>
                  <div style={{ flex: 1 }}>
                    <FloorLayoutViewer
                      layoutImage={lockerFloor?.layout}
                      spaceDefaults={lockerFloor?.spaceDefaults}
                      spaces={lockerSpaces as FloorSpace[]}
                      parkingTypes={lockerTypes as any}
                      onSpaceClick={handleViewSpaceClick}
                    />
                  </div>
                  <div style={{
                    width: '280px',
                    borderLeft: '1px solid #E5E7EB',
                    paddingLeft: '20px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      marginBottom: '16px'
                    }}>
                      Type Legend
                    </h4>
                    <TypeLegend parkingTypes={lockerTypes as any} iconType="locker" />

                    {lockerAddons.length > 0 && (
                      <>
                        <h4 style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151',
                          marginTop: '24px',
                          marginBottom: '16px'
                        }}>
                          Addon Legend
                        </h4>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '12px'
                        }}>
                          {lockerAddons.map((addon) => (
                            <div key={addon._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: addon.color || '#FFA500',
                                border: '1.5px solid #ffffff',
                                boxShadow: '0 0 0 1px #D1D5DB',
                                flexShrink: 0
                              }} />
                              <span style={{ fontSize: '13px', color: '#374151' }}>
                                {addon.name}
                              </span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'locker-spaces' && (
          <div className="model-details-tile">
            <div className="model-details-tile-header">
              <div style={{ flex: 1 }}>
                <h3>Locker Spaces</h3>
                <div style={{ fontSize: '14px', color: '#6B7280', fontWeight: '400' }}>
                  Total: {lockerFloor.spaces?.total || lockerFloor.total || 0} |
                  Available: {lockerFloor.spaces?.available || lockerFloor.available || 0} |
                  Sold: {lockerFloor.spaces?.sold || lockerFloor.sold || 0}
                </div>
              </div>
              <button
                onClick={() => setShowBulkModal(true)}
                className="btn btn-primary btn-sm"
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Plus size={16} /> Bulk Add Spaces
              </button>
            </div>
            <div className="model-details-tile-content" style={{ padding: 0 }}>
              {loadingSpaces ? (
                <div style={{
                  padding: '40px',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center'
                }}>
                  <div className="builders-loading-spinner" />
                </div>
              ) : lockerSpaces.length === 0 ? (
                <div className="model-details-empty">
                  <p>No locker spaces on this floor</p>
                </div>
              ) : (
                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>
                          Space Number
                        </th>
                        <th>
                          Type
                        </th>
                        <th>
                          Status
                        </th>
                        <th>
                          Premium
                        </th>
                        <th>
                          Assigned Unit
                        </th>
                        <th>
                          Addons
                        </th>
                        <th style={{
                          width: '120px'
                        }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {lockerSpaces.map((space, index) => (
                        <tr
                          key={space._id}
                          style={{
                            borderBottom: '1px solid #E5E7EB',
                            backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F3F0'
                          }}
                        >
                          <td className="data-table-primary-cell">
                            {space.number}
                          </td>
                          <td style={{ color: '#374151' }}>
                            {space.type}
                          </td>
                          <td>
                            <span className={getStatusBadgeClass(space.status)}>
                              {space.status}
                            </span>
                          </td>
                          <td style={{ color: '#374151' }}>
                            {space.isPremium ? (
                              <span style={{
                                color: '#48182F',
                                fontWeight: '600'
                              }}>
                                Yes
                              </span>
                            ) : (
                              <span style={{ color: '#9CA3AF' }}>No</span>
                            )}
                          </td>
                          <td style={{ color: '#374151' }}>
                            {space.unitNo || '-'}
                          </td>
                          <td style={{ color: '#374151' }}>
                            {space.addons && space.addons.length > 0 ? (
                              <div style={{ fontSize: '13px' }}>
                                {space.addons.map((addon, idx) => (
                                  <span key={addon._id}>
                                    {addon.name}
                                    {addon.mandatory && <span style={{ color: '#48182F', marginLeft: '4px' }}>*</span>}
                                    {idx < space.addons!.length - 1 && ', '}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: '#9CA3AF' }}>-</span>
                            )}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', width: '100px' }}>
                              <button
                                onClick={() => handleEditSpace(space)}
                                className="btn btn-icon btn-sm"
                                style={{
                                  padding: '6px',
                                  color: '#48182F',
                                  border: 'none',
                                  background: 'transparent'
                                }}
                                title="Edit Space"
                              >
                                <Pencil size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteSpace(space._id)}
                                className="btn btn-icon btn-sm"
                                style={{
                                  padding: '6px',
                                  color: '#DC2626',
                                  border: 'none',
                                  background: 'transparent'
                                }}
                                title="Delete Space"
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Floor Layout Editor Modal */}
      {showEditor && (
        <FabricFloorLayoutEditor
          workspaceId={workspaceId}
          floorId={lockerFloorId}
          projectId={lockerFloor?.projectId || ''}
          entityType="locker"
          floorName={lockerFloor?.name || 'Locker Floor'}
          projectName={lockerFloor?.projectName || 'Project'}
          layoutImage={lockerFloor?.layout}
          spaceDefaults={lockerFloor?.spaceDefaults || { width: 150, height: 75 }}
          parkingTypes={lockerTypes as any}
          parkingAddons={lockerAddons as any}
          spaces={lockerSpaces as FloorSpace[]}
          onClose={() => setShowEditor(false)}
          onSave={handleSavePositions}
          onSpaceClick={handleSpaceClick}
        />
      )}

      {/* Locker Space Edit Modal */}
      {showEditModal && selectedSpace && (
        <LockerSpaceEditModal
          isOpen={showEditModal}
          workspaceId={workspaceId}
          projectId={lockerFloor?.projectId || ''}
          space={selectedSpace}
          onClose={() => {
            setShowEditModal(false);
            setSelectedSpace(null);
          }}
          onSave={handleSpaceSaved}
          onDelete={handleDeleteSpace}
        />
      )}

      {/* Bulk Space Modal */}
      {showBulkModal && (
        <BulkSpaceModal
          isOpen={showBulkModal}
          workspaceId={workspaceId}
          projectId={lockerFloor?.projectId || ''}
          floorId={lockerFloorId}
          entityType="locker"
          spaceDefaults={lockerFloor?.spaceDefaults}
          layoutDimensions={lockerFloor?.layout ? { width: lockerFloor.layout.width, height: lockerFloor.layout.height } : undefined}
          existingSpaces={lockerSpaces}
          onClose={() => setShowBulkModal(false)}
          onSave={handleBulkSpaceSave}
        />
      )}

      {/* Space Details View Modal */}
      {showViewModal && selectedSpace && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999,
            }}
            onClick={() => {
              setShowViewModal(false);
              setSelectedSpace(null);
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '12px',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
              zIndex: 10000,
              minWidth: '450px',
              maxWidth: '500px',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#48182F' }}>
                Space Details
              </h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedSpace(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '4px',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Space Number */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Space Number</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{selectedSpace.number}</span>
              </div>

              {/* Type */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Type</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{selectedSpace.type}</span>
              </div>

              {/* Status */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Status</span>
                <span className={getStatusBadgeClass(selectedSpace.status)}>{selectedSpace.status}</span>
              </div>

              {/* Premium */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Premium</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: selectedSpace.isPremium ? '#48182F' : '#9CA3AF' }}>
                  {selectedSpace.isPremium ? 'Yes' : 'No'}
                </span>
              </div>

              {/* Assigned Unit */}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                <span style={{ color: '#6B7280', fontSize: '14px' }}>Assigned Unit</span>
                <span style={{ fontWeight: 600, fontSize: '14px', color: selectedSpace.unitNo ? '#111827' : '#9CA3AF' }}>
                  {selectedSpace.unitNo || '-'}
                </span>
              </div>

              {/* Addons */}
              {selectedSpace.addons && selectedSpace.addons.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '8px' }}>
                  <span style={{ color: '#6B7280', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Add-ons</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedSpace.addons.map((addon) => (
                      <span
                        key={addon._id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: 'white',
                          borderRadius: '16px',
                          fontSize: '13px',
                          color: '#374151',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        {addon.color && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: addon.color,
                            }}
                          />
                        )}
                        {addon.name}
                        {addon.mandatory && (
                          <span style={{ color: '#48182F', fontWeight: 600 }}>*</span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Mandatory Addons */}
              {selectedSpace.mandatoryAddons && selectedSpace.mandatoryAddons.length > 0 && (
                <div style={{ padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '8px' }}>
                  <span style={{ color: '#92400E', fontSize: '14px', display: 'block', marginBottom: '8px' }}>Mandatory Add-ons</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedSpace.mandatoryAddons.map((addon) => (
                      <span
                        key={addon._id}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: 'white',
                          borderRadius: '16px',
                          fontSize: '13px',
                          color: '#92400E',
                          border: '1px solid #FCD34D',
                        }}
                      >
                        {addon.color && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: addon.color,
                            }}
                          />
                        )}
                        {addon.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedSpace(null);
                }}
                className="btn btn-secondary btn-md"
                style={{ padding: '8px 20px' }}
              >
                Close
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LockerFloorDetailsSlider;
